import React, { useEffect, useState } from 'react';
import { withStyles, Dialog, Paper, DialogTitle, DialogContent, Button, Box } from '@material-ui/core';
import PresetForm from '../../sc-preset';
import { snackActions } from '../../sc-utilities/SnackBar';
import { CancelToken } from 'axios';
import { connect } from "react-redux";
import * as actions from '../../sc-customer/Customer/store/customerActions';
import * as skuActions from '../../sc-settings/SKUPattern/store/skuPatternActions';
import AppLogoLoading from '../../sc-logo';
import * as Yup from "yup";
import { fetchSecurazeSolutions } from '../../sc-admin/SecurazeSolution/store/securazeSolutionActions';
import AndroidSetupForm from "./Mobile/AndroidSetupForm";

const styles = theme => ({
    root: {
        width: `98.9%`,
        height: 'auto',
        paddingLeft: theme.spacing(),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    },
    paper: {
        backgroundColor: "transparent",
        boxShadow: "none",
    },
    form: {
        width: 'auto',
        height: 'auto',
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        paddingTop: theme.spacing(2),
        marginLeft: theme.spacing(0),
        marginRight: theme.spacing(0),
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(4),
        paddingBottom: theme.spacing(1)
    },
    btn: {
        color: theme.palette.buttonText.default,
        borderRadius: '1px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.buttonText.default
        }
    },
});

export function valueText(value) {
    return `${value}%`;
}

export const enabledField = (values) => {
    return !values?.PresetsEnabled;
};

const generalInitialValues = {
    OrderID: null,
    TransportColliID: null,
    ColliID: null,
    PrinterID: null,
    GradeID: null,
    ProcessingSessionEndMessage: null,
}

const workInitialValues = {
    PresetsEnabled: false,
    AutoEnableRemoteAccess: true,
    AutoStartErasure: false,
    ...generalInitialValues,
    AutoStartDiagnose: false,
    AutoShutdown: true,
    NormalScreenBrightness: 100,
    ReducedScreenBrightness: 80,
    ErasureSSD: {
        Method: 'securaze_ssd_performance',
        Verification: {
            Method: 'minimum',
            CustomValue: 10
        }
    },
    ErasureHDD: {
        Method: 'securaze_ssd_performance',
        Verification: {
            Method: 'minimum',
            CustomValue: 10
        }
    },
    automaticUnfreeze: false,
    UsePresetSmartScore: false,
    PresetSmartScore: null,
    SmartHealthCalculation: null,
    OperatingSystemID: null,
    SKUPatternID: null,
    AutoPrint: false,
    PrintUponConnection: false,
    PrintUponSuccessfulErasure: false,
    PrintUponFailedOrAbortedErasure: false,
    PrintUponDiagnose: false,
}

const mobileInitialValues = {
    PrintUponConnection: false,
    PrintUponSuccessfulErasure: false,
    PrintUponFailedOrAbortedErasure: false,
    PrintUponDiagnose: false,
    RemoveAppAfterDiagnose: false,
    ErasureAfterDiagnose: false,
    Type: null,
    Method: 'securaze_ssd',
    Verification: {
        Method: 'minimum',
        CustomValue: 10
    },
    UserKPI: {
        Enabled: false,
        CountDiagnosedDevices: false,
        CountRegisteredDevices: false,
    },
    SKUPatternID: null,
}

const workVerifierInitialValues = {
    AutoShutdown: false,
    AutoStartVerification: false,
    VerificationPercentDone: 100,
    NormalScreenBrightness: 100,
    ReducedScreenBrightness: 80,
}

export const securazeInitialValues = {
    Normal: {
        ...workInitialValues,
        BiosPasswords: { Enabled: false, Passwords: [] },
    },
    LooseDrives: {
        ...workInitialValues,
        BiosPasswords: { Enabled: false, Passwords: [] },
    },
    ...workInitialValues,
    ErasureMobile: {
        Android: {
            ...mobileInitialValues
        },
        iOS: {
            ...mobileInitialValues
        }
    },
    WorkVerifier: workVerifierInitialValues,
    DifferentMobileSolutions: false,
    Wifis: [],
    CollectMobileOpsgeolocation: false,
    AutoshutdownMobile: false,
    AutoshutdownMobileTime: null,
    EnableCustomization: false,
    Customization: null
}

const Presets = props => {
    const { classes, onFetchErasureMethods, activeCustomerId, translations, fetchOrders, fetchCollies, fetchGrades, fetchOperatingSystems, fetchPrinters, onFetchSecurazeSolutions, loadPresets, updatePresets, loadedPreset, idParams, onLoadCustomer, customerUpdatePresets, customer, userEmail, sendAndroidSetup, userLanguage, fetchSKUPattern } = props;

    const [loading, setLoading] = useState(true);
    const [customerPresetFilter, setCustomerPresetsFilter] = useState({ orders: [], grades: [], printers: [], Collies: [], operatingSystems: [], securazeSolutions: [], skuPatterns: [] });
    const { orders, grades, printers, operatingSystems, Collies, securazeSolutions, methods, skuPatterns } = customerPresetFilter;

    const [openDialog, setOpenDialog] = useState(false);

    const handleSendEmail = (formData) => {
        const params = {
            Email: formData.Email,
            ActiveCustomerID: activeCustomerId,
            userLanguage,
        };

        if(formData.Wifi) { params.Wifi = formData.Wifi};

        sendAndroidSetup(params)
        setOpenDialog(false)
    }

    const formSettingsAndroidSetupForm = {
        Validation: {
            Email: Yup.string().email().required("Required"),
        },
        ButtonSubmit: {
            Title: translations["send_email"] || "Send email",
            Color: "primary"
        },
    };

    let presets = customer && customer.Presets && Object.keys(customer.Presets).length > 1 ? JSON.parse(customer.Presets) : securazeInitialValues;
    if (loadedPreset) {
        presets = loadedPreset.Presets && Object.keys(loadedPreset.Presets).length > 1 ? JSON.parse(loadedPreset.Presets) : securazeInitialValues;
    }

    const presetsCancel = CancelToken?.source();
    const customerCancel = CancelToken?.source();

    const ordersCancel = CancelToken?.source();
    const colliesCancel = CancelToken?.source();
    const gradesCancel = CancelToken.source();
    const printersCancel = CancelToken.source();
    const osCancel = CancelToken.source();
    const requestToken = CancelToken.source();

    const collectPresets = (data) => {
        const ErasureMobile = data.ErasureMobile ?? {};

        if (!data.DifferentMobileSolutions) {
            data.ErasureMobile["iOS"] = { ...data.ErasureMobile["Android"] }
        }

        const getWorkEraseOnlyValues = (data, eraseOnlyName) => {
            return {
                PresetsEnabled: data?.[eraseOnlyName] ? !!data?.[eraseOnlyName]?.PresetsEnabled : !!data?.PresetsEnabled,
                AutoEnableRemoteAccess: data?.[eraseOnlyName] ? !!data?.[eraseOnlyName]?.AutoEnableRemoteAccess : !!data?.AutoEnableRemoteAccess,
                // AutoPrint: data?.[eraseOnlyName] ? !!data?.[eraseOnlyName]?.AutoPrint : !!data?.AutoPrint,
                AutoShutdown: data?.[eraseOnlyName] ? !!data?.[eraseOnlyName]?.AutoShutdown : !!data?.AutoShutdown,
                AutoStartErasure: data?.[eraseOnlyName] ? !!data?.[eraseOnlyName]?.AutoStartErasure : !!data?.AutoStartErasure,
                AutoStartDiagnose: data?.[eraseOnlyName] ? !!data?.[eraseOnlyName]?.AutoStartDiagnose : !!data?.AutoStartDiagnose,
                OrderID: data?.[eraseOnlyName] ? data?.[eraseOnlyName]?.OrderID : data?.OrderID,
                TransportColliID: data?.[eraseOnlyName] ? data?.[eraseOnlyName]?.TransportColliID : data?.TransportColliID,
                ColliID: data?.[eraseOnlyName] ? data?.[eraseOnlyName]?.ColliID : data?.ColliID,
                NormalScreenBrightness: (data?.[eraseOnlyName] ? data?.[eraseOnlyName]?.NormalScreenBrightness : data?.NormalScreenBrightness) ?? 100,
                ReducedScreenBrightness: (data?.[eraseOnlyName] ? data?.[eraseOnlyName]?.ReducedScreenBrightness : data?.ReducedScreenBrightness) ?? 80,
                ErasureSSD: {
                    Method: data?.[eraseOnlyName] ? data?.[eraseOnlyName]?.MethodSSD : data?.MethodSSD,
                    Verification: {
                        Method: data?.[eraseOnlyName] ? data?.[eraseOnlyName]?.VerificationSSD : data?.VerificationSSD,
                        CustomValue: data?.[eraseOnlyName] ? data?.[eraseOnlyName]?.CustomValueSSD : data?.CustomValueSSD
                    }
                },
                ErasureHDD: {
                    Method: data?.[eraseOnlyName] ? data?.[eraseOnlyName]?.MethodHDD : data?.MethodHDD,
                    Verification: {
                        Method: data?.[eraseOnlyName] ? data?.[eraseOnlyName]?.VerificationHDD : data?.VerificationHDD,
                        CustomValue: data?.[eraseOnlyName] ? data?.[eraseOnlyName]?.CustomValueHDD : data?.CustomValueHDD
                    }
                },
                automaticUnfreeze: data?.[eraseOnlyName] ? !!data?.[eraseOnlyName]?.automaticUnfreeze : !!data?.automaticUnfreeze,
                UsePresetSmartScore: data?.[eraseOnlyName] ? !!data?.[eraseOnlyName]?.UsePresetSmartScore : !!data?.UsePresetSmartScore,
                PresetSmartScore: data?.[eraseOnlyName] ? data?.[eraseOnlyName]?.PresetSmartScore : data?.PresetSmartScore,
                SmartHealthCalculation: data?.[eraseOnlyName] ? data?.[eraseOnlyName]?.SmartHealthCalculation : data?.SmartHealthCalculation,
                PrinterID: data?.[eraseOnlyName] ? data?.[eraseOnlyName]?.PrinterID : data?.PrinterID,
                GradeID: data?.[eraseOnlyName] ? data?.[eraseOnlyName]?.GradeID : data?.GradeID,
                OperatingSystemID: data?.[eraseOnlyName] ? data?.[eraseOnlyName]?.OperatingSystemID : data?.OperatingSystemID,
                ProcessingSessionEndMessage: data?.[eraseOnlyName] ? data?.[eraseOnlyName]?.ProcessingSessionEndMessage : data?.ProcessingSessionEndMessage,
                SKUPatternID: data?.[eraseOnlyName] ? data?.[eraseOnlyName]?.SKUPatternID : data?.SKUPatternID,
                PrintUponConnection: data?.[eraseOnlyName] ? !!data?.[eraseOnlyName]?.PrintUponConnection : !!data?.PrintUponConnection,
                PrintUponSuccessfulErasure: data?.[eraseOnlyName] ? !!data?.[eraseOnlyName]?.PrintUponSuccessfulErasure : !!data?.PrintUponSuccessfulErasure,
                PrintUponFailedOrAbortedErasure: data?.[eraseOnlyName] ? !!data?.[eraseOnlyName]?.PrintUponFailedOrAbortedErasure : !!data?.PrintUponFailedOrAbortedErasure,
                PrintUponDiagnose: data?.[eraseOnlyName] ? !!data?.[eraseOnlyName]?.PrintUponDiagnose : !!data?.PrintUponDiagnose,
            }
        }

        const presetData = {
            Normal: {
                ...getWorkEraseOnlyValues(data, "Normal"),
                BiosPasswords: { ...data.Normal.BiosPasswords, Passwords: data.Normal.BiosPasswords.Passwords.map((password, index) => { return { ...password, Index: index } }) },
            },
            LooseDrives: getWorkEraseOnlyValues(data, "LooseDrives"),
            ...getWorkEraseOnlyValues(data, "Normal"),
            DifferentMobileSolutions: data.DifferentMobileSolutions,
            ErasureMobile: {
                Android: {
                    PrintUponConnection: ErasureMobile.Android?.PrintUponConnection ?? false,
                    PrintUponSuccessfulErasure: ErasureMobile.Android?.PrintUponSuccessfulErasure ?? false,
                    PrintUponFailedOrAbortedErasure: ErasureMobile.Android?.PrintUponFailedOrAbortedErasure ?? false,
                    PrintUponDiagnose: ErasureMobile.Android?.PrintUponDiagnose ?? false,
                    RemoveAppAfterDiagnose: ErasureMobile.Android?.RemoveAppAfterDiagnose ?? false,
                    ErasureAfterDiagnose: ErasureMobile.Android?.ErasureAfterDiagnose ?? false,
                    Type: ErasureMobile.Android?.TypeMobile,
                    Method: ErasureMobile.Android?.MethodMobile,
                    Verification: {
                        Method: ErasureMobile.Android?.VerificationMobile,
                        CustomValue: ErasureMobile.Android?.CustomValueMobile
                    },
                    UserKPI: {
                        Enabled: ErasureMobile?.Android?.UserKPIEnabled,
                        CountDiagnosedDevices: ErasureMobile?.Android?.UserKPICountDiagnosedDevices,
                        CountRegisteredDevices: ErasureMobile?.Android?.UserKPICountRegisteredDevices,
                    }
                },
                iOS: {
                    PrintUponConnection: ErasureMobile.iOS?.PrintUponConnection ?? false,
                    PrintUponSuccessfulErasure: ErasureMobile.iOS?.PrintUponSuccessfulErasure ?? false,
                    PrintUponFailedOrAbortedErasure: ErasureMobile.iOS?.PrintUponFailedOrAbortedErasure ?? false,
                    PrintUponDiagnose: ErasureMobile.iOS?.PrintUponDiagnose ?? false,
                    RemoveAppAfterDiagnose: ErasureMobile.iOS?.RemoveAppAfterDiagnose ?? false,
                    ErasureAfterDiagnose: ErasureMobile.iOS?.ErasureAfterDiagnose ?? false,
                    Type: ErasureMobile.iOS?.TypeMobile,
                    Method: ErasureMobile.iOS?.MethodMobile,
                    Verification: {
                        Method: ErasureMobile.iOS?.VerificationMobile,
                        CustomValue: ErasureMobile.iOS?.CustomValueMobile
                    },
                    UserKPI: {
                        Enabled: ErasureMobile?.iOS?.UserKPIEnabled,
                        CountDiagnosedDevices: ErasureMobile?.iOS?.UserKPICountDiagnosedDevices,
                        CountRegisteredDevices: ErasureMobile?.iOS?.UserKPICountRegisteredDevices,
                    }
                },
                SKUPatternID: ErasureMobile?.SKUPatternID
            },
            Wifis: data.Wifis,
            CollectMobileOpsgeolocation: data?.CollectMobileOpsgeolocation,
            EnableCustomization: !!data?.EnableCustomization,
            AutoshutdownMobile: !!data?.AutoshutdownMobile,
            AutoshutdownMobileTime: data?.AutoshutdownMobileTime,
            Customization: !!data?.EnableCustomization ? {
                Work: {
                    Texts: data?.WorkTexts,
                    Headlines: data?.WorkHeadlines
                },
                Mobile: {
                    Texts: data?.MobileTexts,
                    Headlines: data?.MobileHeadlines
                },
                Remote: {
                    Texts: data?.RemoteTexts,
                    Headlines: data?.RemoteHeadlines
                },
                BackgroundColor: data?.BackgroundColor,
                ForegroundColor: data?.ForegroundColor,
                Logo: data?.Logo
            } : null
        }
        return presetData;
    }

    const handleLoadPresets = () => {
        loadPresets ? loadPresets() : onLoadCustomer({ CustomerID: activeCustomerId, ActiveCustomerID: activeCustomerId }, { cancelToken: customerCancel });
    }

    const handleUpdatePresets = async (presets) => {
        return await updatePresets ? updatePresets({ Presets: presets, ...idParams, ActiveCustomerID: activeCustomerId }) : customerUpdatePresets({ Presets: presets, CustomerID: customer?.CustomerID, ActiveCustomerID: activeCustomerId })
    }

    useEffect(() => {
        const fetchData = async () => {
            let skuPatterns = await fetchSKUPattern({ ActiveCustomerID: activeCustomerId });
            let orderData = await fetchOrders({ ActiveCustomerID: activeCustomerId, q: { Status: ['new', 'scheduled', 'inWarehouse', 'onHold'] }, ...idParams }, { cancelToken: ordersCancel });
            let collies = await fetchCollies({ ActiveCustomerID: activeCustomerId, q: { Status: [null, 'Active'], Locked: false, Finished: ['0'] }, ...idParams }, { cancelToken: colliesCancel });
            let grades = await fetchGrades({ ActiveCustomerID: activeCustomerId, ...idParams }, { cancelToken: gradesCancel });
            let printers = await fetchPrinters({ ActiveCustomerID: activeCustomerId, ...idParams }, { cancelToken: printersCancel });
            let operatingSystems = await fetchOperatingSystems({ ActiveCustomerID: activeCustomerId, ...idParams }, { cancelToken: osCancel });
            let methods = await onFetchErasureMethods({ ActiveCustomerID: activeCustomerId })
            let securazeSolutions = await onFetchSecurazeSolutions({ ActiveCustomerID: activeCustomerId });

            if (orderData && collies && grades && printers && operatingSystems && securazeSolutions && methods) {
                const selectSecurazeSolutions = securazeSolutions?.map((solution) => {
                    return {
                        value: solution.Key, displayValue: solution.LongName
                    }
                })
                setCustomerPresetsFilter({
                    skuPatterns,
                    orders: orderData,
                    Collies: collies,
                    printers: [{ value: null, displayValue: translations['none'] || 'None' }, ...printers],
                    grades: [{ value: null, displayValue: translations['none'] || 'None' }, ...grades],
                    operatingSystems: [{ value: null, displayValue: translations['none'] || 'None' }, ...operatingSystems],
                    securazeSolutions: [{ value: null, displayValue: translations['none'] || 'None' }, ...selectSecurazeSolutions],
                    methods: [{ value: null, displayValue: translations['none'] || 'None' }, ...methods]
                });
            }
            setLoading(false);
        }

        handleLoadPresets();

        fetchData();

        return () => {
            ordersCancel.cancel('Operation canceled by the user.');
            customerCancel.cancel('Operation canceled by user');
            colliesCancel.cancel('Operation canceled by the user.');
            gradesCancel.cancel('Operation canceled by the user.');
            printersCancel.cancel('Operation canceled by the user.');
            osCancel.cancel('Operation canceled by the user.');
            presetsCancel.cancel('Operation canceled by the user.');
            requestToken.cancel('Operation canceled by the user.');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeCustomerId]);

    const initialMobile = {
        Android: {
            PrintUponConnection: presets?.ErasureMobile?.Android?.PrintUponConnection || presets?.ErasureMobile?.PrintUponConnection || null,
            PrintUponSuccessfulErasure: presets?.ErasureMobile?.Android?.PrintUponSuccessfulErasure || presets?.ErasureMobile?.PrintUponSuccessfulErasure || null,
            PrintUponFailedOrAbortedErasure: presets?.ErasureMobile?.Android?.PrintUponFailedOrAbortedErasure || presets?.ErasureMobile?.PrintUponFailedOrAbortedErasure || null,
            PrintUponDiagnose: presets?.ErasureMobile?.Android?.PrintUponDiagnose || presets?.ErasureMobile?.PrintUponDiagnose || null,
            RemoveAppAfterDiagnose: presets?.ErasureMobile?.Android?.RemoveAppAfterDiagnose || presets?.ErasureMobile?.RemoveAppAfterDiagnose || null,
            ErasureAfterDiagnose: presets?.ErasureMobile?.Android?.ErasureAfterDiagnose || presets?.ErasureMobile?.ErasureAfterDiagnose || null,
            TypeMobile: presets?.ErasureMobile?.Android?.Type || presets?.ErasureMobile?.Type || null,
            MethodMobile: presets?.ErasureMobile?.Android?.Method || presets?.ErasureMobile?.Method || null,
            VerificationMobile: presets?.ErasureMobile?.Android?.Verification?.Method || presets?.ErasureMobile?.Verification?.Method || null,
            CustomValueMobile: presets?.ErasureMobile?.Android?.Verification?.CustomValue || presets?.ErasureMobile?.Verification?.CustomValue || null,
            UserKPIEnabled: presets?.ErasureMobile?.Android?.UserKPI?.Enabled ?? presets?.ErasureMobile?.UserKPI?.Enabled ?? false,
            UserKPICountDiagnosedDevices: presets?.ErasureMobile?.Android?.UserKPI?.CountDiagnosedDevices ?? presets?.ErasureMobile?.UserKPI?.CountDiagnosedDevices ?? true,
            UserKPICountRegisteredDevices: presets?.ErasureMobile?.Android?.UserKPI?.CountRegisteredDevices ?? presets?.ErasureMobile?.UserKPI?.CountRegisteredDevices ?? true,
        },
        iOS: {
            PrintUponConnection: presets?.ErasureMobile?.iOS?.PrintUponConnection || presets?.ErasureMobile?.PrintUponConnection || null,
            PrintUponSuccessfulErasure: presets?.ErasureMobile?.iOS?.PrintUponSuccessfulErasure || presets?.ErasureMobile?.PrintUponSuccessfulErasure || null,
            PrintUponFailedOrAbortedErasure: presets?.ErasureMobile?.iOS?.PrintUponFailedOrAbortedErasure || presets?.ErasureMobile?.PrintUponFailedOrAbortedErasure || null,
            PrintUponDiagnose: presets?.ErasureMobile?.iOS?.PrintUponDiagnose || presets?.ErasureMobile?.PrintUponDiagnose || null,
            RemoveAppAfterDiagnose: presets?.ErasureMobile?.iOS?.RemoveAppAfterDiagnose || presets?.ErasureMobile?.RemoveAppAfterDiagnose || null,
            ErasureAfterDiagnose: presets?.ErasureMobile?.iOS?.ErasureAfterDiagnose || presets?.ErasureMobile?.ErasureAfterDiagnose || null,
            TypeMobile: presets?.ErasureMobile?.iOS?.Type || presets?.ErasureMobile?.Type || null,
            MethodMobile: presets?.ErasureMobile?.iOS?.Method || presets?.ErasureMobile?.Method || null,
            VerificationMobile: presets?.ErasureMobile?.iOS?.Verification?.Method || presets?.ErasureMobile?.Verification?.Method || null,
            CustomValueMobile: presets?.ErasureMobile?.iOS?.Verification?.CustomValue || presets?.ErasureMobile?.Verification?.CustomValue || null,
            UserKPIEnabled: presets?.ErasureMobile?.iOS?.UserKPI?.Enabled ?? presets?.ErasureMobile?.UserKPI?.Enabled ?? false,
            UserKPICountDiagnosedDevices: presets?.ErasureMobile?.iOS?.UserKPI?.CountDiagnosedDevices ?? presets?.ErasureMobile?.UserKPI?.CountDiagnosedDevices ?? true,
            UserKPICountRegisteredDevices: presets?.ErasureMobile?.iOS?.UserKPI?.CountRegisteredDevices ?? presets?.ErasureMobile?.UserKPI?.CountRegisteredDevices ?? true,
        },
        SKUPatternID: presets?.ErasureMobile?.SKUPatternID || ''
    }
    
    const getInitialWork = (eraseOnlyName) => {
        const initial = presets && presets[eraseOnlyName] ? presets[eraseOnlyName] : presets;
        return {
            PresetsEnabled: initial?.PresetsEnabled,
            AutoShutdown: initial?.AutoShutdown,
            OrderID: initial?.OrderID || '',
            TransportColliID: initial?.TransportColliID || '',
            ColliID: initial?.ColliID || "",
            AutoStartErasure: initial?.AutoStartErasure,
            AutoEnableRemoteAccess: initial?.AutoEnableRemoteAccess,
            MethodSSD: initial?.ErasureSSD?.Method || null,
            VerificationSSD: initial?.ErasureSSD?.Verification?.Method || null,
            CustomValueSSD: initial?.ErasureSSD?.Verification?.CustomValue || null,
            MethodHDD: initial?.ErasureHDD?.Method || null,
            VerificationHDD: initial?.ErasureHDD?.Verification?.Method || null,
            CustomValueHDD: initial?.ErasureHDD?.Verification?.CustomValue || null,
            AutoStartDiagnose: initial?.AutoStartDiagnose,
            AutoPrint: initial?.AutoPrint,
            automaticUnfreeze: initial?.automaticUnfreeze,
            UsePresetSmartScore: initial?.UsePresetSmartScore,
            PresetSmartScore: initial?.PresetSmartScore,
            PrinterID: initial?.PrinterID || "",
            GradeID: initial?.GradeID || "",
            OperatingSystemID: initial?.OperatingSystemID || "",
            ProcessingSessionEndMessage: initial?.ProcessingSessionEndMessage,
            NormalScreenBrightness: initial?.NormalScreenBrightness ?? 100,
            ReducedScreenBrightness: initial?.ReducedScreenBrightness ?? 80,
            SmartHealthCalculation: initial?.SmartHealthCalculation,
            SKUPatternID: initial?.SKUPatternID || '',
            PrintUponConnection: initial?.PrintUponConnection || null,
            PrintUponSuccessfulErasure: initial?.PrintUponSuccessfulErasure || null,
            PrintUponFailedOrAbortedErasure: initial?.PrintUponFailedOrAbortedErasure || null,
            PrintUponDiagnose: initial?.PrintUponDiagnose || null,
        }
    }

    const initialValuesPreset = {
        Normal: {
            ...getInitialWork("Normal"),
            BiosPasswords: presets ? presets.Normal.BiosPasswords ?? securazeInitialValues.Normal.BiosPasswords : securazeInitialValues.Normal.BiosPasswords,
        },
        LooseDrives: getInitialWork("LooseDrives"),

        DifferentMobileSolutions: presets ? !!presets?.DifferentMobileSolutions : securazeInitialValues.DifferentMobileSolutions,
        ErasureMobile: initialMobile,

        // PrinterID: presets?.PrinterID || '',
        // GradeID: presets?.GradeID || '',
        // OperatingSystemID: presets?.OperatingSystemID || '',
        Wifis: presets ? presets.Wifis ?? [] : [],
        // ProcessingSessionEndMessage: presets?.ProcessingSessionEndMessage ?? null,
        CollectMobileOpsgeolocation: !!(presets?.CollectMobileOpsgeolocation) ?? false,
        AutoshutdownMobile: !!(presets?.AutoshutdownMobile) ?? false,
        AutoshutdownMobileTime: presets?.AutoshutdownMobileTime || null,

        EnableCustomization: presets ? !!presets?.EnableCustomization : securazeInitialValues.EnableCustomization,
        WorkTexts: presets?.Customization?.Work?.Texts || null,
        WorkHeadlines: presets?.Customization?.Work?.Headlines || null,

        MobileTexts: presets?.Customization?.Mobile?.Texts || null,
        MobileHeadlines: presets?.Customization?.Mobile?.Headlines || null,

        RemoteTexts: presets?.Customization?.Remote?.Texts || null,
        RemoteHeadlines: presets?.Customization?.Remote?.Headlines || null,

        BackgroundColor: presets?.Customization?.BackgroundColor || "#0C5A99",
        ForegroundColor: presets?.Customization?.ForegroundColor || "#0C5A99",
        Logo: presets?.Customization?.Logo || null
    };

    const wifiObject = Yup.object().shape({
        Security: Yup.string().required(),
        SSID: Yup.string().required(),
        Password: Yup.string().required(),
        Username: Yup.string().when("Security", {
            is: (value) => ["wpa_enterprise", "wpa2_enterprise", "wpa3_enterprise"].includes(value),
            then: Yup.string().required()
        })
    });

    const biosPasswordObject = Yup.object().shape({
        Description: Yup.string().required(),
        Password: Yup.string().required(),
    });

    let formSettingsPreset = {
        Validation: {
            Wifis: Yup.array().of(wifiObject),
            Normal: Yup.object().shape({
                BiosPasswords: Yup.object().shape({
                    Enabled: Yup.boolean().required(),
                    Passwords: Yup.array().of(biosPasswordObject)
                }),
            }),
        },
        ButtonSubmit: {
            Title: translations["save"] || "SAVE",
            Color: 'primary'
        }
    };

    const handleSubmit = async data => {
        const presets = collectPresets(data);

        const serverResult = await handleUpdatePresets(presets);
        handleLoadPresets();

        setLoading(false);
        if (serverResult.status) {
            return snackActions.snackBarComponent(translations['update_presets'] || "Presets updated.", "success");
        } else {
            return snackActions.snackBarComponent(translations['failed_to_update_presets'] || "Error: Failed to update presets", "error");
        }
    };

    return (
        <div className={classes.root} >
            <Dialog disableBackdropClick={true} disableEscapeKeyDown={false} open={loading}
                PaperProps={{
                    classes: {
                        root: classes.paper
                    }
                }}
            >
                <AppLogoLoading />
            </Dialog>
            <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                aria-labelledby="qr-dialog-title"
                disableBackdropClick={false}
                disableEscapeKeyDown={false}
                fullWidth={true}
            >
                <DialogTitle id="qr-dialog-title">{translations['send_qr_code'] || 'Send QR code for automatic android setup'}</DialogTitle>
                <DialogContent>
                    <AndroidSetupForm translations={translations}
                        handleSubmit={handleSendEmail}
                        initialValues={{ Email: userEmail }}
                        formSettings={formSettingsAndroidSetupForm}
                        wifis={presets.Wifis} />
                </DialogContent>
            </Dialog>
            <Paper className={classes.form}>
                <Box
                    m={1}
                    display="flex"
                    justifyContent="flex-end"                 
                >
                    <Button
                        style={{ marginLeft: '30' }}
                        className={classes.btn}
                        variant="contained"
                        color={formSettingsAndroidSetupForm.ButtonSubmit.Color}
                        type="button"
                        onClick={() => setOpenDialog(true)}
                    >
                        {translations['qr_code_for_android'] || 'QR code for android setup'}
                    </Button>
                </Box>
                <PresetForm
                    formSettings={formSettingsPreset}
                    initialValues={initialValuesPreset}
                    customerID={activeCustomerId}
                    handleSubmit={handleSubmit}
                    loadPresets={handleLoadPresets}
                    translations={translations}
                    setLoading={setLoading}
                    customerPresetFilter={{ operatingSystems: operatingSystems, grades: grades, printers: printers, orders: orders, collies: Collies, securazeSolutions: securazeSolutions, methods: methods, skuPatterns }}
                    updatePresets={handleUpdatePresets}
                    securazeInitialValues={securazeInitialValues}
                    idParams={idParams}
                    userEmail={userEmail}
                />
            </Paper>
        </div>
    )
};
const mapStateToProps = state => {
    return {
        activeCustomerId: state?.core?.login?.activeCustomer?.CustomerID || null,
        customer: state?.customer?.customers?.loaded || null,
        permissions: state?.core?.login?.permissions || [],
        translations: state?.translate?.translations?.activeTranslations || {},
        userEmail: state?.core?.login?.user?.Email || '',
        userLanguage: state?.core?.login?.userSettings?.DefaultLanguage || 'en',
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onLoadCustomer: (customerID, options) => dispatch(actions.loadCustomer(customerID, options)),
        customerUpdatePresets: (params, options) => dispatch(actions.updatePresets(params, options)),
        onFetchErasureMethods: (params) => (dispatch(actions.fetchErasureMethod(params))),
        fetchOrders: (id, options) => dispatch(actions.fetchOrders(id, options)),
        fetchCollies: (id, options) => dispatch(actions.fetchCollies(id, options)),
        fetchPrinters: (id, options) => dispatch(actions.fetchPrinters(id, options)),
        fetchGrades: (id, options) => dispatch(actions.fetchGrades(id, options)),
        fetchOperatingSystems: (params, options) => dispatch(actions.fetchOperatingSystems(params, options)),
        onFetchSecurazeSolutions: (data) => dispatch(fetchSecurazeSolutions(data)),
        sendAndroidSetup: (data) => dispatch(actions.sendAndroidSetup(data)),
        fetchSKUPattern: (data) => dispatch(skuActions.fetchSKUPattern(data))
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(Presets));

